/**
 * Outbox processor — drains queued sales-order submissions when the device
 * is online. Started once at app boot via startOutboxProcessor().
 *
 * Triggers:
 *   - window 'online' event
 *   - background interval (default 30s)
 *   - manual drainOutbox() call (e.g. from /outbox "retry now" buttons)
 *
 * Per entry:
 *   - mark sending, POST to /api/v1/sales-orders with the entry's idempotency
 *     key, on success mark sent with the returned orderNo + spireId.
 *   - network failures (ApiError.kind='network') stay pending — increment
 *     attempts, retry later. After MAX_NETWORK_ATTEMPTS, mark failed so the
 *     user has to retry manually (probably auth or a deeper problem).
 *   - server-side validation (4xx) marks failed immediately — retrying won't
 *     help unless the user edits the request.
 */

import { ApiError, api } from './api.js';
import {
  enqueueFieldEvent,
  getOutboxDb,
  listDrainable,
  listDrainableFieldEvents,
  markFailed,
  markFieldEventFailed,
  markFieldEventSending,
  markExtrasEnqueued,
  markFieldEventSent,
  markOrderCreated,
  markSending,
  markSent,
  markSignatureAttached,
  pruneSent,
  pruneSentFieldEvents,
  type FieldEventEntry,
  type SalesOrderOutboxEntry,
} from './outbox.js';

const RETRY_INTERVAL_MS = 30_000;
// Sent rows linger a full day so a rep can find their Spire order number in
// /outbox long after submitting (60s pruning made successful submits look
// like they vanished — audit P0-12).
const SENT_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_NETWORK_ATTEMPTS = 8;

let timer: ReturnType<typeof setInterval> | null = null;
let onlineListener: (() => void) | null = null;
let draining = false;

/**
 * Idempotent — calling twice is a no-op. Returns a stop() function for tests
 * and HMR.
 */
export function startOutboxProcessor(): () => void {
  if (timer !== null) return stopOutboxProcessor;

  void drainOutbox().catch(reportError);

  timer = setInterval(() => {
    void drainOutbox().catch(reportError);
  }, RETRY_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    onlineListener = () => {
      void drainOutbox().catch(reportError);
    };
    window.addEventListener('online', onlineListener);
  }

  return stopOutboxProcessor;
}

export function stopOutboxProcessor(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  if (typeof window !== 'undefined' && onlineListener) {
    window.removeEventListener('online', onlineListener);
    onlineListener = null;
  }
}

/**
 * Try to drain every drainable entry. Concurrency=1 — we don't want two POSTs
 * racing for the same entryId. Skips entirely if the browser reports offline.
 */
export async function drainOutbox(): Promise<{ sent: number; failed: number; pending: number }> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { sent: 0, failed: 0, pending: 0 };
  }
  if (draining) return { sent: 0, failed: 0, pending: 0 };
  draining = true;
  let sent = 0;
  let failed = 0;
  let pending = 0;

  try {
    const db = getOutboxDb();
    const entries = await listDrainable(db);
    for (const entry of entries) {
      const result = await sendOne(entry);
      if (result === 'sent') sent += 1;
      else if (result === 'failed') failed += 1;
      else pending += 1;
    }
    // Drain field-tech captures (parts / photos / notes / signatures) with
    // the same per-entry retry semantics. Errors don't block subsequent
    // entries — each drainable entry tries on its own merits.
    const fieldEvents = await listDrainableFieldEvents(db);
    for (const fe of fieldEvents) {
      const result = await sendFieldEvent(fe);
      if (result === 'sent') sent += 1;
      else if (result === 'failed') failed += 1;
      else pending += 1;
    }
    await pruneSent(SENT_TTL_MS, db);
    await pruneSentFieldEvents(SENT_TTL_MS, db);
  } finally {
    draining = false;
  }
  return { sent, failed, pending };
}

/**
 * Drain one field-event entry. The dispatch is by payload.kind:
 *   - addItem            → POST /sales-orders/:id/items
 *   - addPhoto           → POST /sales-orders/:id/photos
 *   - addNote            → POST /sales-orders/:id/notes
 *   - attachSignature    → POST /sales-orders/:id/signature
 *
 * Each path returns a Spire-side id (lineId for items, noteId for the
 * rest) which we persist for audit. Idempotency keys are only forwarded
 * on the addItem path right now — the others rely on Spire's own dedupe
 * because the spire-client photo/note/signature endpoints don't accept
 * an idempotency key.
 */
async function sendFieldEvent(entry: FieldEventEntry): Promise<SendOutcome> {
  await markFieldEventSending(entry.entryId);
  try {
    let resultId: number;
    switch (entry.payload.kind) {
      case 'addItem': {
        const out = await api.salesOrders.addItem(
          entry.spireSalesOrderId,
          entry.payload.data,
          entry.idempotencyKey,
        );
        resultId = out.lineId;
        break;
      }
      case 'addPhoto': {
        const out = await api.salesOrders.addPhoto(
          entry.spireSalesOrderId,
          entry.payload.data,
        );
        // Null when the Spire mirror failed — the photo is still safely in
        // Tinsmith's store, so the event is done either way.
        resultId = out.noteId ?? 0;
        break;
      }
      case 'addNote': {
        const out = await api.salesOrders.addNote(
          entry.spireSalesOrderId,
          entry.payload.data,
        );
        resultId = out.noteId;
        break;
      }
      case 'attachSignature': {
        const out = await api.salesOrders.attachSignature(
          entry.spireSalesOrderId,
          entry.payload.data,
        );
        resultId = out.noteId;
        break;
      }
    }
    await markFieldEventSent(entry.entryId, resultId);
    return 'sent';
  } catch (err) {
    return handleFieldEventFailure(entry, err);
  }
}

function handleFieldEventFailure(
  entry: FieldEventEntry,
  err: unknown,
): Promise<SendOutcome> {
  const message = err instanceof Error ? err.message : String(err);
  if (err instanceof ApiError) {
    if (err.kind === 'network' || err.status >= 500 || err.status === 0) {
      const wouldExceed = entry.attempts + 1 >= MAX_NETWORK_ATTEMPTS;
      return markFieldEventFailed(entry.entryId, message, {
        permanent: wouldExceed,
      }).then(() => (wouldExceed ? 'failed' : 'pending'));
    }
    // Auth failures are session-state, not payload-state: the capture is
    // fine and will succeed once the user re-authenticates (or the session
    // keeper silently refreshes). Never permanent, never capped — the
    // entry just waits for the next drain with a valid token.
    // 401/403 = session state; 423 = the order is open (locked) in Spire
    // desktop. Neither says anything about the capture itself.
    if (err.status === 401 || err.status === 403 || err.status === 423) {
      return markFieldEventFailed(entry.entryId, message, { permanent: false }).then(
        () => 'pending',
      );
    }
    return markFieldEventFailed(entry.entryId, message, { permanent: true }).then(
      () => 'failed',
    );
  }
  return markFieldEventFailed(entry.entryId, message, { permanent: false }).then(
    () => 'pending',
  );
}

type SendOutcome = 'sent' | 'failed' | 'pending';

async function sendOne(entry: SalesOrderOutboxEntry): Promise<SendOutcome> {
  await markSending(entry.entryId);

  // ── Step 1: Create the order (skip if it's already been created in a
  //    prior partial attempt — lastSpireId is the marker for that).
  let spireId = entry.lastSpireId;
  let orderNo = entry.lastOrderNo;
  if (spireId === null || orderNo === null) {
    try {
      const order = await api.salesOrders.create(entry.body, entry.idempotencyKey);
      spireId = order.spireId;
      orderNo = order.orderNo;
      await markOrderCreated(entry.entryId, { orderNo, spireId });
    } catch (err) {
      return handleStepFailure(entry, err);
    }
  }

  // ── Step 2: Upload the signature, if any.
  const signatureStillPending =
    entry.signature !== null && entry.signatureNoteId === null;
  if (signatureStillPending && entry.signature !== null) {
    try {
      const result = await api.salesOrders.attachSignature(spireId, {
        contentType: entry.signature.contentType,
        base64: entry.signature.base64,
        subject: 'Customer signature (Tinsmith)',
      });
      await markSignatureAttached(entry.entryId, result.noteId);
    } catch (err) {
      return handleStepFailure(entry, err);
    }
  }

  // ── Step 3: Fan the quote's photos + written note out into the
  // field-event outbox. Those rows get their own retry/outbox UI, and the
  // drain loop picks them up right after this entry. `extrasEnqueued` makes
  // the fan-out one-shot across retries of this entry.
  const photos = entry.photos ?? [];
  const note = entry.note?.trim() ?? '';
  if (!entry.extrasEnqueued && (photos.length > 0 || note.length > 0)) {
    try {
      // Explicit increasing createdAt keeps the drain order: note first,
      // then photos in the order the rep added them.
      let seq = Date.now();
      if (note.length > 0) {
        await enqueueFieldEvent({
          spireSalesOrderId: spireId,
          spireOrderNo: orderNo,
          payload: { kind: 'addNote', data: { body: note, subject: entry.noteSubject ?? QUOTE_NOTE_SUBJECT } },
          createdAt: seq++,
        });
      }
      for (const photo of photos) {
        await enqueueFieldEvent({
          spireSalesOrderId: spireId,
          spireOrderNo: orderNo,
          payload: { kind: 'addPhoto', data: { ...photo, caption: 'Quote photo' } },
          createdAt: seq++,
        });
      }
      await markExtrasEnqueued(entry.entryId);
    } catch (err) {
      return handleStepFailure(entry, err);
    }
  }

  await markSent(entry.entryId, { orderNo, spireId });
  return 'sent';
}

/** CRM-note subject for a note written on the quote (vs 'Tinsmith field note'). */
export const QUOTE_NOTE_SUBJECT = 'Tinsmith quote note';

function handleStepFailure(entry: SalesOrderOutboxEntry, err: unknown): Promise<SendOutcome> {
  const message = err instanceof Error ? err.message : String(err);
  if (err instanceof ApiError) {
    if (err.kind === 'network' || err.status >= 500 || err.status === 0) {
      const wouldExceed = entry.attempts + 1 >= MAX_NETWORK_ATTEMPTS;
      return markFailed(entry.entryId, message, { permanent: wouldExceed }).then(() =>
        wouldExceed ? 'failed' : 'pending',
      );
    }
    // Expired/invalid session: retryable forever — the order will go
    // through after re-auth. (Pre-fix this was permanent, which killed a
    // day's offline quotes the moment the hourly token died.)
    // ... and 423 (order locked in Spire desktop) clears by itself once the
    // other user saves or closes it — e.g. the signature / photo step of a
    // just-created order someone opened in Spire.
    if (err.status === 401 || err.status === 403 || err.status === 423) {
      return markFailed(entry.entryId, message, { permanent: false }).then(() => 'pending');
    }
    return markFailed(entry.entryId, message, { permanent: true }).then(() => 'failed');
  }
  return markFailed(entry.entryId, message, { permanent: false }).then(() => 'pending');
}

function reportError(err: unknown): void {
  // eslint-disable-next-line no-console
  console.warn('[outbox] drain failed', err);
}
